import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { setAuth0TokenRetriever, setAuth0Logout } from './services/api';
import useIdleTimeout from './hooks/useIdleTimeout';
import './App.css';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import ModelPage from './pages/ModelPage';
import ModelsPage from './pages/ModelsPage';
import CallbackPage from './pages/CallbackPage';
import FeaturesPage from './pages/FeaturesPage';
import SupportPage from './pages/SupportPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import ErrorBoundary from './components/ErrorBoundary';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import { useTheme } from './contexts/ThemeContext';
import { ResultProvider } from './contexts/ResultContext';
import UtilityPage from './pages/UtilityPage';
import EtailPage from './pages/EtailPage';
import { handleClarityConsent } from './utils/clarity'; // Make sure the path is correct


function App() {
  const { isLoading, getAccessTokenSilently, logout, isAuthenticated, user } = useAuth0();

  const [consentGiven, setConsentGiven] = useState();
  const [showBanner, setShowBanner] = useState(true);

  // This is a simplified example. In a real application, you would
  // likely check for a cookie or local storage value to see if
  // the user has already given consent.
  useEffect(() => {
    // For this example, we assume consent is not given on first visit.
    // In a real app, check for a 'clarity-consent' cookie or similar.

    setConsentGiven(localStorage.getItem('clarity-consent'))
  }, []);

  const giveConsent = () => {
    handleClarityConsent();
    setConsentGiven(true);
    // You should also set a cookie or local storage item here
    // to remember the user's consent choice.
    localStorage.setItem('clarity-consent', 'true');
  };

  // const handleIdleLogout = useCallback(() => {
  //   if (isAuthenticated) {
  //     console.log('User timed out due to inactivity or token issue. Logging out.');
  //     //logout({ logoutParams: { returnTo: window.location.origin } });
  //   }
  // }, [logout, isAuthenticated]);

  //useIdleTimeout(15 * 60 * 1000, handleIdleLogout);

  useEffect(() => {
    setAuth0TokenRetriever(() => getAccessTokenSilently());
    setAuth0Logout((options) => logout(options));

    return () => {
      setAuth0TokenRetriever(null);
      setAuth0Logout(null);
    };
  }, [getAccessTokenSilently, logout]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated && !user.email_verified) {
    return (
      <Router>
        <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900">
          <main className="flex-grow">
            <Routes>
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="*" element={<Navigate to="/verify-email" replace />} />
            </Routes>
          </main>
        </div>
      </Router>
    );
  }

  return (
    <ResultProvider>
      <Router>
        <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900">
          <Navbar />
          <main className="flex-grow">
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/callback" element={<CallbackPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/etail" element={<EtailPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route
                  path="/dashboard"
                  element={<ProtectedRoute component={DashboardPage} />}
                />
                <Route
                  path="/models"
                  element={<ProtectedRoute component={ModelsPage} />}
                />
                <Route
                  path="/model/:modelId"
                  element={<ProtectedRoute component={ModelPage} />}
                />
                <Route
                  path="/utility/:uId"
                  element={<ProtectedRoute component={UtilityPage} />}
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ErrorBoundary>
          </main>
          <Footer />
          {/* This is a simple consent banner example. */}
          {!consentGiven && (
            <div className="fixed bottom-0 w-full bg-white shadow-lg border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-700 text-center sm:text-left">
                We use cookies to improve your experience. By clicking{" "}
                <span className="font-semibold">"Accept"</span>, you consent to our use of cookies.
              </p>
              <div>
                <button
                  onClick={() => setConsentGiven(true)}
                  className="px-4 py-2 mr-6 bg-gray-400 text-white text-sm font-medium rounded-lg shadow hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={giveConsent}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg shadow hover:bg-blue-700 transition-colors"
                >
                  Accept
                </button>
              </div>
            </div>


            // <div style={{
            //   position: 'fixed',
            //   bottom: 0,
            //   width: '100%',
            //   backgroundColor: '#f0f0f0',
            //   color: '#000',
            //   padding: '20px',
            //   textAlign: 'center',
            //   borderTop: '1px solid #ccc'
            // }}>
            //   <p>We use cookies to improve your experience. By clicking "Accept", you consent to our use of cookies.</p>
            //   <button onClick={giveConsent}>Accept</button>
            // </div>
          )}
        </div>
        <Analytics />
        <SpeedInsights />
      </Router>
    </ResultProvider>
  );
}

export default App;
